// Reunderwriting Module (Tier 3) PR 3 — case-level assignment widget.
//
// Mirror of components/DealFlow/DealAssignmentPills, adapted for cases:
// every reunderwriting case carries the same 7 role slots (C1, B1, A1,
// A2, A3, A4, B2) as a deal, with C1/B1 seeded NotApplicable per the
// PR 2 ReunderwritingAssignmentSeedingService.
//
// Pill state mapping (matches the deal pills, Underwriting spec §2.3):
//   Unassigned     → outlined, muted text.
//   Assigned       → filled gold, onyx text.
//   NotApplicable  → strikethrough, muted red, no fill.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { extractErrorMessage } from "@/api/client";
import {
  useReunderwritingAssignments,
  useUpsertReunderwritingAssignment,
  useMarkAssignmentNotApplicable,
} from "@/hooks/reunderwriting/useReunderwriting";
import { useAuth } from "@/hooks/useAuth";
import {
  REUNDERWRITING_PERMISSIONS,
  type ReunderwritingAssignmentDto,
  type ReunderwritingStage,
} from "@/types/reunderwriting";
import type { DealAssignmentStatus } from "@/api/dealFlow/types";

const REUNDERWRITING_ROLE_CODES = ["C1", "B1", "A1", "A2", "A3", "A4", "B2"] as const;
type ReunderwritingRoleCode = (typeof REUNDERWRITING_ROLE_CODES)[number];

const ROLE_LABEL: Record<ReunderwritingRoleCode, string> = {
  C1: "Broker Liaison",
  B1: "Sr Loan Admin",
  A1: "Underwriter",
  A2: "Financial Analyst",
  A3: "Legal Analyst",
  A4: "Market & Credit Research",
  B2: "Closing Coordinator",
};

// Per spec: A2/A3/A4/B2 only assignable once the case is in Underwriting+.
// All Reunderwriting cases launch into Underwriting so this is effectively
// always-true here, but we keep the gate so a Declined/Withdrawn case
// shows the pills as read-only.
const STAGE_GATED_ROLES: ReadonlySet<ReunderwritingRoleCode> = new Set([
  "A2", "A3", "A4", "B2",
]);

interface PillsProps {
  caseId: string;
  stage: ReunderwritingStage;
  /** Compact mode (Kanban card / list row) — smaller pills, no popover. */
  compact?: boolean;
}

function byRole(
  list: ReunderwritingAssignmentDto[] | undefined,
): Partial<Record<ReunderwritingRoleCode, ReunderwritingAssignmentDto>> {
  const out: Partial<Record<ReunderwritingRoleCode, ReunderwritingAssignmentDto>> = {};
  for (const a of list ?? []) {
    out[a.roleCode as ReunderwritingRoleCode] = a;
  }
  return out;
}

export function ReunderwritingAssignmentPills({
  caseId,
  stage,
  compact = false,
}: PillsProps) {
  const { data, isLoading } = useReunderwritingAssignments(caseId);

  const byRoleMap = useMemo(() => byRole(data), [data]);
  const stageGated = (role: ReunderwritingRoleCode): boolean =>
    STAGE_GATED_ROLES.has(role) &&
    stage !== "Underwriting" &&
    stage !== "Closing" &&
    stage !== "Closed";

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-space-1"
        data-testid="reunderwriting-assignment-pills-skeleton"
      >
        {REUNDERWRITING_ROLE_CODES.map((r) => (
          <span
            key={r}
            className={`inline-flex items-center justify-center rounded-pill border border-border-light bg-white-warm animate-pulse ${
              compact ? "h-5 w-8 text-[10px]" : "h-7 w-10 text-caption"
            }`}
            aria-hidden
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-space-1"
      role="group"
      aria-label="Reunderwriting case assignments"
      data-testid="reunderwriting-assignment-pills"
    >
      {REUNDERWRITING_ROLE_CODES.map((role) => (
        <Pill
          key={role}
          caseId={caseId}
          role={role}
          assignment={byRoleMap[role]}
          compact={compact}
          stageGated={stageGated(role)}
        />
      ))}
    </div>
  );
}

interface PillProps {
  caseId: string;
  role: ReunderwritingRoleCode;
  assignment: ReunderwritingAssignmentDto | undefined;
  compact: boolean;
  stageGated: boolean;
}

function Pill({ caseId, role, assignment, compact, stageGated }: PillProps) {
  const status: DealAssignmentStatus = assignment?.status ?? "Unassigned";

  const sizeClass = compact ? "h-5 px-space-1 text-[10px]" : "h-7 px-space-2 text-caption";
  const stateClass = stateClassName(status, stageGated);
  const baseClass = `inline-flex items-center justify-center font-display font-bold uppercase tracking-label rounded-pill border transition-colors ${sizeClass} ${stateClass}`;

  const titleParts: string[] = [`${role} — ${ROLE_LABEL[role]}`];
  if (status === "Assigned") titleParts.push("Assigned");
  else if (status === "NotApplicable") titleParts.push("Marked Not Applicable for this case");
  else titleParts.push(stageGated ? "Assignable once the case reaches Underwriting" : "Unassigned · click to assign");

  if (compact || stageGated) {
    return (
      <span
        className={baseClass}
        title={titleParts.join(" · ")}
        aria-label={titleParts.join(" · ")}
        data-testid={`reunderwriting-pill-${role}`}
        data-status={status}
        data-stage-gated={stageGated ? "true" : undefined}
      >
        {role}
      </span>
    );
  }

  return (
    <AssignmentPopover
      caseId={caseId}
      role={role}
      assignment={assignment}
      trigger={
        <button
          type="button"
          className={`${baseClass} hover:ring-2 hover:ring-gold/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold`}
          title={titleParts.join(" · ")}
          aria-label={titleParts.join(" · ")}
          data-testid={`reunderwriting-pill-${role}`}
          data-status={status}
        >
          {role}
        </button>
      }
    />
  );
}

function stateClassName(status: DealAssignmentStatus, stageGated: boolean): string {
  if (stageGated) {
    return "border-border-light bg-white-warm text-gray-mid opacity-60 cursor-not-allowed";
  }
  switch (status) {
    case "Assigned":
      return "bg-gold text-onyx border-gold";
    case "NotApplicable":
      return "border-status-error/40 text-status-error line-through";
    case "Unassigned":
    default:
      return "border-border-light text-gray-mid bg-white-pure";
  }
}

interface PopoverProps {
  caseId: string;
  role: ReunderwritingRoleCode;
  assignment: ReunderwritingAssignmentDto | undefined;
  trigger: ReactNode;
}

function AssignmentPopover({ caseId, role, assignment, trigger }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <div ref={triggerRef} onClick={() => setOpen((v) => !v)}>
        {trigger}
      </div>
      {open && (
        <div
          ref={popoverRef}
          className="absolute z-40 top-full left-0 mt-space-1 w-[280px] bg-white-pure border border-border-light rounded-md shadow-lg p-space-3"
          role="dialog"
          aria-label={`Assign ${role}`}
        >
          <PopoverBody
            caseId={caseId}
            role={role}
            assignment={assignment}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function PopoverBody({
  caseId,
  role,
  assignment,
  onClose,
}: {
  caseId: string;
  role: ReunderwritingRoleCode;
  assignment: ReunderwritingAssignmentDto | undefined;
  onClose: () => void;
}) {
  const toast = useToast();
  const auth = useAuth();
  const permSet = new Set(auth.user?.permissions ?? []);
  const upsert = useUpsertReunderwritingAssignment(caseId);
  const markNa = useMarkAssignmentNotApplicable(caseId);

  const currentStatus = assignment?.status ?? "Unassigned";
  const [status, setStatus] = useState<DealAssignmentStatus>(currentStatus);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(
    assignment?.assignedUserId ?? null,
  );

  const canAssign = permSet.has(REUNDERWRITING_PERMISSIONS.AssignTeam);
  const canSelfClaim = permSet.has(REUNDERWRITING_PERMISSIONS.SelfClaimSlot) &&
    (auth.user?.roleCodes ?? []).includes(role);
  const canMarkNa = permSet.has(REUNDERWRITING_PERMISSIONS.MarkSlotNotApplicable);

  async function handleSave() {
    try {
      if (status === "NotApplicable") {
        await markNa.mutateAsync(role);
        toast.success(`${role} marked Not Applicable.`);
      } else if (status === "Assigned" && assignedUserId) {
        await upsert.mutateAsync({
          roleCode: role,
          body: { status: "Assigned", assignedUserId },
        });
        toast.success(`${role} assigned.`);
      } else {
        await upsert.mutateAsync({
          roleCode: role,
          body: { status: "Unassigned", assignedUserId: null },
        });
        toast.success(`${role} unassigned.`);
      }
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleSelfClaim() {
    if (!auth.user?.id) return;
    try {
      await upsert.mutateAsync({
        roleCode: role,
        body: { status: "Assigned", assignedUserId: auth.user.id },
      });
      toast.success(`Claimed ${role}.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div>
        <h4 className="font-display text-h4 m-0">{role} — {ROLE_LABEL[role]}</h4>
        <p className="text-caption text-gray-mid m-0">Reunderwriting case assignment.</p>
      </div>

      {canSelfClaim && currentStatus === "Unassigned" && (
        <Button onClick={handleSelfClaim} disabled={upsert.isPending}>
          Claim {role} for myself
        </Button>
      )}

      <fieldset className="flex flex-col gap-space-1">
        <legend className="text-caption text-gray-mid">Status</legend>
        <label className="inline-flex items-center gap-space-2 text-small">
          <input
            type="radio"
            checked={status === "Unassigned"}
            onChange={() => setStatus("Unassigned")}
            disabled={!canAssign}
          />
          Unassigned
        </label>
        <label className="inline-flex items-center gap-space-2 text-small">
          <input
            type="radio"
            checked={status === "Assigned"}
            onChange={() => setStatus("Assigned")}
            disabled={!canAssign}
          />
          Assigned
        </label>
        <label className="inline-flex items-center gap-space-2 text-small">
          <input
            type="radio"
            checked={status === "NotApplicable"}
            onChange={() => setStatus("NotApplicable")}
            disabled={!canMarkNa}
          />
          Not applicable for this case
        </label>
      </fieldset>

      {status === "Assigned" && canAssign && (
        <RoleFilteredUserPicker
          role={role}
          value={assignedUserId}
          onChange={setAssignedUserId}
        />
      )}

      <div className="flex items-center justify-end gap-space-2">
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={
            upsert.isPending ||
            markNa.isPending ||
            (status === "Assigned" && !assignedUserId)
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
}
